package com.sddamico.draftmachine.ds.brand

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span

enum class LogoVariant { Full, Stacked, Mark }

/**
 * The Draft Machine wordmark lockup — a copper cog-mark with a riveted hub beside the stamped Bungee
 * wordmark. The mark is a Phosphor gear glyph (placeholder for commissioned art). Port of
 * `components/brand/Logo.jsx`.
 */
@Composable
fun Logo(
  variant: LogoVariant = LogoVariant.Full,
  size: Double = 40.0,
  onDark: Boolean = true,
  modifier: Modifier = Modifier,
) {
  val words = size * 0.6
  val textColor = if (onDark) DmColor.steel100 else DmColor.steel900

  when (variant) {
    LogoVariant.Mark -> Span(attrs = modifier.displayInlineFlex().toAttrs()) { LogoMark(size) }

    LogoVariant.Stacked -> Span(
      attrs = modifier.displayInlineFlex().flexDirection(FlexDirection.Column)
        .alignItems(AlignItems.Center).gap((size * 0.2).px).toAttrs()
    ) {
      LogoMark(size)
      Span(
        attrs = Modifier.displayInlineFlex().flexDirection(FlexDirection.Column)
          .alignItems(AlignItems.Center).lineHeight(0.9).toAttrs()
      ) {
        Word("DRAFT", words, textColor)
        Word("MACHINE", words, textColor, Modifier.margin(top = (size * 0.04).px))
      }
    }

    LogoVariant.Full -> Span(
      attrs = modifier.displayInlineFlex().alignItems(AlignItems.Center).gap((size * 0.34).px).toAttrs()
    ) {
      LogoMark(size)
      Span(attrs = Modifier.displayInlineFlex().alignItems(AlignItems.Baseline).gap(0.34.em).toAttrs()) {
        Word("DRAFT", words, textColor)
        Word("MACHINE", words, textColor)
      }
    }
  }
}

/** A stamped Bungee wordmark segment. */
@Composable
private fun Word(text: String, size: Double, color: Color, modifier: Modifier = Modifier) {
  SpanText(
    text,
    modifier.fontFamily(*DmType.display).fontSize(size.px).letterSpacing(0.005.em).lineHeight(1).color(color)
  )
}

/** The copper cog-mark: a textured brass gear glyph with a riveted steel hub. */
@Composable
private fun LogoMark(size: Double) {
  Span(
    attrs = Modifier.position(Position.Relative).displayInlineFlex()
      .width(size.px).height(size.px).flex("0 0 auto").toAttrs()
  ) {
    // Textured copper gear, clipped to the glyph.
    Icon(
      "gear-six", IconWeight.Fill,
      Modifier.fontSize(size.px).styleModifier {
        property("background", "${DmEffects.textureBrushed}, #C07A44")
        property("-webkit-background-clip", "text")
        property("background-clip", "text")
        property("-webkit-text-fill-color", "transparent")
        property("filter", "drop-shadow(0 1px 1px rgba(0,0,0,.55))")
      }
    )
    // Riveted steel hub.
    Span(
      attrs = Modifier.position(Position.Absolute).styleModifier {
        property("top", "50%"); property("left", "50%"); property("transform", "translate(-50%,-50%)")
        property("width", "${size * 0.34}px"); property("height", "${size * 0.34}px")
        property("border-radius", "50%")
        property("background", "radial-gradient(circle at 38% 34%, #6A7B8A 0%, #35424E 55%, #161C22 100%)")
        property("box-shadow", "inset 0 1px 1px rgba(232,240,247,.35), inset 0 -2px 3px rgba(0,0,0,.6)")
        property("border", "1.5px solid #161C22")
      }.toAttrs()
    ) {}
  }
}
